import { Button, Flex, Modal, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineDelete, AiOutlineReload } from "react-icons/ai";

import type { CredentialItem } from "sandbox/types";

import { CopyButton } from "sandbox/features/shared";

import s from "./CredentialCard.module.css";

interface CredentialCardProps {
  credential: CredentialItem;
  onDelete: (id: string) => void;
  onRotate: (id: string) => void;
}

export const CredentialCard = ({
  credential,
  onDelete,
  onRotate,
}: CredentialCardProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <article className={s.card}>
      <Flex align="center" justify="space-between" gap="middle">
        <h3 className={s.name}>{credential.name}</h3>
        <Button
          type="text"
          size="small"
          icon={<AiOutlineDelete />}
          onClick={() => setConfirmDelete(true)}
          aria-label={t("sandbox:components.credentialCard.deleteCredential")}
        />
      </Flex>

      <Flex vertical gap={4}>
        <span className={s.fieldLabel}>{t("common:labels.clientId")}</span>
        <Flex align="center" gap="small" className={s.fieldRow}>
          <code className={s.codeChip}>{credential.clientId}</code>
          <CopyButton
            value={credential.clientId}
            label={t("sandbox:components.credentialCard.copyClientId")}
          />
        </Flex>
      </Flex>

      <Button
        icon={<AiOutlineReload />}
        block
        onClick={() => onRotate(credential.id)}
      >
        {t("sandbox:components.credentialCard.regenerateSecretKey")}
      </Button>

      <Modal
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        title={t("sandbox:components.credentialCard.confirmDelete.title", {
          name: credential.name,
        })}
        okText={t("common:components.buttons.yesDelete")}
        okButtonProps={{ danger: true }}
        cancelText={t("common:components.buttons.cancel")}
        onOk={() => {
          onDelete(credential.id);
          setConfirmDelete(false);
        }}
      >
        <Typography.Paragraph>
          {t("sandbox:components.credentialCard.confirmDelete.description")}
        </Typography.Paragraph>
        <Typography.Paragraph>
          {t("sandbox:components.credentialCard.confirmDelete.confirmation")}
        </Typography.Paragraph>
      </Modal>
    </article>
  );
};
